import { Answers } from "../../UserJourney/UserJourneyMap";

// GPPAQ — General Practice Physical Activity Questionnaire (DH, updated May 2009).
//
// The 4-level Physical Activity Index (PAI) is computed exactly as the guidance's Annex 1:
// only occupation, physical exercise hours and cycling hours count. Walking, housework/childcare,
// gardening/DIY and walking pace are collected but never scored (guidance §4D: over-reported
// during validation). The guidance warns that modifying the coding algorithm negates the
// questionnaire's validation, so the two tables below are transcribed cell for cell.
//
// "Not in employment" has no column in Annex 1B; it is scored as Sedentary, as the DH GPPAQ
// Excel template and the EPIC index it derives from do.

const totalRiskLevel = 4;

const GppaqGuidelineDefinition = [
    { key: "inactive", label: "ranges.inactive", riskLevel: 4 / totalRiskLevel },
    { key: "moderatelyInactive", label: "ranges.moderately_inactive", riskLevel: 3 / totalRiskLevel },
    { key: "moderatelyActive", label: "ranges.moderately_active", riskLevel: 2 / totalRiskLevel },
    { key: "active", label: "ranges.active", riskLevel: 1 / totalRiskLevel }
];

const GppaqGuidelineDisplayOrder = [...GppaqGuidelineDefinition].sort((a, b) => b.riskLevel - a.riskLevel);

// Hour bands, as row/column indexes into the Annex 1 tables.
const NONE = 0;
const UNDER_ONE = 1;
const ONE_TO_THREE = 2;
const THREE_PLUS = 3;

const HOURS_BAND = {
    [Answers.gppaq.hoursNone]: NONE,
    [Answers.gppaq.hoursUnderOne]: UNDER_ONE,
    [Answers.gppaq.hoursOneToThree]: ONE_TO_THREE,
    [Answers.gppaq.hoursThreePlus]: THREE_PLUS
};

// Annex 1C — combining physical exercise (rows) and cycling (columns).
const COMBINED_HOURS = [
    [NONE, UNDER_ONE, ONE_TO_THREE, THREE_PLUS],
    [UNDER_ONE, ONE_TO_THREE, THREE_PLUS, THREE_PLUS],
    [ONE_TO_THREE, THREE_PLUS, THREE_PLUS, THREE_PLUS],
    [THREE_PLUS, THREE_PLUS, THREE_PLUS, THREE_PLUS]
];

const SEDENTARY = 0;
const STANDING = 1;
const PHYSICAL = 2;
const HEAVY_MANUAL = 3;

const OCCUPATION_COLUMN = {
    [Answers.gppaq.notEmployed]: SEDENTARY,
    [Answers.gppaq.sedentary]: SEDENTARY,
    [Answers.gppaq.standing]: STANDING,
    [Answers.gppaq.physical]: PHYSICAL,
    [Answers.gppaq.heavyManual]: HEAVY_MANUAL
};

// Annex 1B — combined exercise + cycling (rows) against occupation (columns).
const PAI = [
    ["inactive", "moderatelyInactive", "moderatelyActive", "active"],
    ["moderatelyInactive", "moderatelyActive", "active", "active"],
    ["moderatelyActive", "active", "active", "active"],
    ["active", "active", "active", "active"]
];

// Choice answers are stored as their English labels, so lookups are on that text.
const normalise = answer =>
    String(answer)
        .trim()
        .toLowerCase();

const byNormalisedAnswer = map =>
    Object.keys(map).reduce((acc, answer) => ({ ...acc, [normalise(answer)]: map[answer] }), {});

const HOURS_BAND_BY_ANSWER = byNormalisedAnswer(HOURS_BAND);
const OCCUPATION_COLUMN_BY_ANSWER = byNormalisedAnswer(OCCUPATION_COLUMN);

const lookup = (map, answer) => (answer == null ? undefined : map[normalise(answer)]);

/**
 * @param {{ occupation: string, exerciseHours: string, cyclingHours: string }} answers - the stored
 *   answers to GPPAQ_OCCUPATION, GPPAQ_EXERCISE_HOURS and GPPAQ_CYCLING_HOURS
 * @returns {{ key: string, label: string, riskLevel: number } | null}
 */
function gppaq({ occupation, exerciseHours, cyclingHours } = {}) {
    const column = lookup(OCCUPATION_COLUMN_BY_ANSWER, occupation);
    const exercise = lookup(HOURS_BAND_BY_ANSWER, exerciseHours);
    const cycling = lookup(HOURS_BAND_BY_ANSWER, cyclingHours);
    if (column === undefined || exercise === undefined || cycling === undefined) return null;

    const key = PAI[COMBINED_HOURS[exercise][cycling]][column];
    return GppaqGuidelineDefinition.find(def => def.key === key) || null;
}

const RiskName = "GPPAQ Physical Activity";
const RiskCode = "GPPAQ";
const GuidelineName = "GPPAQ Physical Activity Index";
const GuidelineCode = "GPPAQ_PAI";

export {
    gppaq,
    GppaqGuidelineDefinition,
    GppaqGuidelineDisplayOrder,
    RiskName,
    RiskCode,
    GuidelineName,
    GuidelineCode
};
